import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// Prevent bug coverage (STRY0038808). Data: GET /dashboard → bugCoverage, one
// entry per enabled Prevent vendor:
//   published  — ODD's published bug total, stored daily by
//                tools-api-publishedBugCounts (null = unknown / no catalog)
//   open       — matched bugs not closed in ServiceNow
//   addressed  — matched bugs closed in ServiceNow (vendor auto-closes included)

// Event-feed vendors: health events / findings, not a published bug catalog.
const NO_CATALOG = ['aws', 'awsconfig', 'awsreshub', 'awssechub', 'azure', 'msft365', 'logmon'];

const unpublishedTitle = vendorId =>
  NO_CATALOG.includes(vendorId)
    ? 'Event feed — this vendor has no published bug catalog'
    : 'Published count not available yet';

// One bugCoverage entry → numbers (published stays null when unknown).
const toCoverage = c => ({
  published: c.published == null ? null : Number(c.published),
  open: Number(c.open) || 0,
  addressed: Number(c.addressed) || 0
});

// Rows joined to vendor names and limited to vendors the user can read
// (VendorsContext, RBAC-filtered), as on the rest of the dashboard — GET
// /dashboard returns every enabled Prevent vendor. Totals are the sum of the
// kept rows so the band and the table always agree. Rows come back unsorted:
// CoverageTable owns the row order (click a column header to sort).
export const buildCoverage = (bugCoverage, vendors) => {
  const byId = {};
  (vendors || []).forEach(v => (byId[v.id] = v));
  const rows = (bugCoverage || [])
    .filter(c => byId[c.vendorId])
    .map(c => ({ vendor: byId[c.vendorId], ...toCoverage(c) }));
  const totals = rows.reduce(
    (acc, r) => {
      acc.published += r.published || 0;
      acc.open += r.open;
      acc.addressed += r.addressed;
      if (r.published != null) acc.catalogVendors += 1;
      return acc;
    },
    { published: 0, open: 0, addressed: 0, catalogVendors: 0 }
  );
  return { rows, totals };
};

const fmt = n => (n == null ? '—' : n.toLocaleString());
const pctAddressed = (open, addressed) => (open + addressed ? Math.round((addressed / (open + addressed)) * 100) : 0);

// Open / addressed are shares of the bugs that MATCHED this tenant (open +
// addressed), never of the published catalog: published is how many bugs we
// watch, matched is how many of them reached this environment.
const NOTHING_MATCHED = 'Nothing has matched your inventory yet';

// Open vs addressed split of the bugs that matched this tenant. Nothing
// matched → just the empty track (no coloured spans).
const SplitBar = ({ open, addressed, width }) => (
  <div className="bz-bar" style={{ width, display: 'flex' }}>
    {open + addressed > 0 && (
      <>
        <span style={{ width: `${100 - pctAddressed(open, addressed)}%`, background: 'var(--bad)', borderRadius: 0 }} />
        <span style={{ width: `${pctAddressed(open, addressed)}%`, background: 'var(--ok)', borderRadius: 0 }} />
      </>
    )}
  </div>
);

const Figure = ({ label, value, sub, color }) => (
  <div style={{ minWidth: 0 }}>
    <div className="bz-stat__label">{label}</div>
    <div className="bz-tnum" style={{ fontSize: 'var(--fs-3xl)', fontWeight: 500, lineHeight: 1.1, color }}>
      {value}
    </div>
    <div className="bz-stat__meta" style={{ marginTop: 4 }}>
      {sub}
    </div>
  </div>
);

const Divider = () => <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--line)' }} />;

// Dashboard headline: published universe → matched to this tenant (open / addressed).
export const CoverageBand = ({ totals }) => {
  const matched = totals.open + totals.addressed;
  return (
    <div className="bz-card" style={{ padding: '18px 20px', borderTop: '3px solid var(--prevent)' }}>
      <div className="bz-row" style={{ justifyContent: 'space-between', marginBottom: 14 }}>
        <div className="bz-kicker" style={{ color: 'var(--prevent)' }}>
          Bug coverage
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr auto 1fr auto 1fr auto 1.2fr', gap: 20 }}>
        <Figure
          label="Monitoring"
          value={totals.catalogVendors ? fmt(totals.published) : '—'}
          sub={`published vendor bugs across ${totals.catalogVendors} ${
            totals.catalogVendors === 1 ? 'vendor' : 'vendors'
          }`}
        />
        <Divider />
        <Figure
          label="Open in your environment"
          value={fmt(totals.open)}
          sub="matched your inventory, still open in ServiceNow"
          color="var(--bad)"
        />
        <Divider />
        <Figure label="Addressed" value={fmt(totals.addressed)} sub="closed in ServiceNow" color="var(--ok)" />
        <Divider />
        <div style={{ alignSelf: 'center' }}>
          <div className="bz-stat__label" style={{ marginBottom: 8 }}>
            {matched ? `${pctAddressed(totals.open, totals.addressed)}% of matched bugs addressed` : 'Remediation'}
          </div>
          <SplitBar open={totals.open} addressed={totals.addressed} width="100%" />
          <div className="bz-stat__meta" style={{ marginTop: 8 }}>
            {matched ? `${fmt(totals.open)} of ${fmt(matched)} matched bugs still open` : NOTHING_MATCHED}
          </div>
        </div>
      </div>
    </div>
  );
};

// Sortable columns of the per-vendor table. `get` returns the value to order
// by; null means "no value" (published unknown, or nothing affecting so there
// is no remediation %) and sorts last in BOTH directions. `num` columns start
// descending (biggest first), text columns ascending.
const COLS = [
  { key: 'vendor', label: 'Vendor', num: false, get: r => r.vendor.name || '' },
  { key: 'published', label: 'Published bugs', num: true, get: r => r.published },
  { key: 'open', label: 'Open in your environment', num: true, get: r => r.open },
  { key: 'addressed', label: 'Addressed', num: true, get: r => r.addressed },
  {
    key: 'remediation',
    label: 'Remediation',
    num: true,
    get: r => (r.open + r.addressed ? pctAddressed(r.open, r.addressed) : null)
  }
];

const byVendorName = (a, b) => (a.vendor.name || '').localeCompare(b.vendor.name || '');

// Missing values last in both directions; ties fall back to vendor name A→Z.
const sortCoverage = (rows, sortKey, sortDir) => {
  const col = COLS.find(c => c.key === sortKey);
  const dir = sortDir === 'asc' ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const av = col.get(a);
    const bv = col.get(b);
    const aNull = av == null;
    const bNull = bv == null;
    if (aNull && bNull) return byVendorName(a, b);
    if (aNull) return 1;
    if (bNull) return -1;
    const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
    return cmp * dir || byVendorName(a, b);
  });
};

const Chev = () => (
  <svg
    className="bz-th-sort__chev"
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

// Dashboard per-vendor table. Default order: published bugs, biggest first.
export const CoverageTable = ({ rows, onConfigure }) => {
  const [sortKey, setSortKey] = useState('published');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => sortCoverage(rows || [], sortKey, sortDir), [rows, sortKey, sortDir]);

  // Same column flips direction; a new column starts at its natural direction.
  const onSort = col => {
    if (col.key === sortKey) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(col.key);
      setSortDir(col.num ? 'desc' : 'asc');
    }
  };

  return (
    <div className="bz-card">
      <div
        className="bz-row"
        style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-soft)', justifyContent: 'space-between' }}
      >
        <h3 className="bz-h3">Coverage by vendor</h3>
        <div className="bz-row" style={{ gap: 8 }}>
          <span className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
            All time
          </span>
        </div>
      </div>
      <table className="bz-table">
        <thead>
          <tr>
            {COLS.map(c => (
              <th
                key={c.key}
                className="bz-th-sort"
                aria-sort={sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <button type="button" onClick={() => onSort(c)}>
                  {c.label}
                  <Chev />
                </button>
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {sorted.map(r => (
            <tr key={r.vendor.id}>
              <td style={{ fontWeight: 500 }}>{r.vendor.name}</td>
              <td className="bz-mono" title={r.published == null ? unpublishedTitle(r.vendor.id) : undefined}>
                {r.published == null ? <span className="bz-muted">—</span> : fmt(r.published)}
              </td>
              <td>
                {r.open > 0 ? (
                  <span className="bz-pill bz-pill--bad bz-mono">{fmt(r.open)}</span>
                ) : (
                  <span className="bz-muted bz-mono">0</span>
                )}
              </td>
              <td>
                {r.addressed > 0 ? (
                  <span className="bz-pill bz-pill--ok bz-mono">{fmt(r.addressed)}</span>
                ) : (
                  <span className="bz-muted bz-mono">0</span>
                )}
              </td>
              <td>
                {r.open + r.addressed > 0 ? (
                  <div className="bz-row" style={{ gap: 8 }}>
                    <SplitBar open={r.open} addressed={r.addressed} width={110} />
                    <span className="bz-muted bz-mono" style={{ fontSize: 'var(--fs-xs)' }}>
                      {pctAddressed(r.open, r.addressed)}%
                    </span>
                  </div>
                ) : (
                  <span className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
                    Nothing affecting
                  </span>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                <a onClick={() => onConfigure(r.vendor.id)} style={{ fontSize: 'var(--fs-sm)', cursor: 'pointer' }}>
                  Configure →
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Compact figure for the vendor configuration header.
const Stat = ({ label, value, color, title }) => (
  <div style={{ textAlign: 'right' }} title={title}>
    <div className="bz-tnum" style={{ fontSize: 'var(--fs-xl)', fontWeight: 500, lineHeight: 1.1, color }}>
      {value}
    </div>
    <div className="bz-stat__meta">{label}</div>
  </div>
);
Stat.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  color: PropTypes.string,
  title: PropTypes.string
};

// Vendor configuration header: compact trio, right-aligned.
// Event-feed vendors (NO_CATALOG) never have a published catalog, so the figure
// is left out rather than shown as a permanent "—".
export const CoverageInline = ({ coverage, vendorId, vendorName }) => {
  const c = toCoverage(coverage);
  const matched = c.open + c.addressed;
  return (
    <div className="bz-row" style={{ marginLeft: 'auto', gap: 20, alignItems: 'center', flexShrink: 0 }}>
      {!NO_CATALOG.includes(vendorId) && (
        <>
          <Stat
            label={c.published == null ? 'published bugs' : `published ${vendorName || ''} bugs`.replace('  ', ' ')}
            value={fmt(c.published)}
            title={c.published == null ? unpublishedTitle(vendorId) : undefined}
          />
          <Divider />
        </>
      )}
      <Stat label="open in your environment" value={fmt(c.open)} color="var(--bad)" />
      <Stat label="addressed" value={fmt(c.addressed)} color="var(--ok)" />
      <div style={{ width: 130 }}>
        <SplitBar open={c.open} addressed={c.addressed} width="100%" />
        <div className="bz-stat__meta" style={{ marginTop: 6, textAlign: 'right' }}>
          {matched ? `${pctAddressed(c.open, c.addressed)}% addressed` : 'nothing matched yet'}
        </div>
        {matched > 0 && (
          <div className="bz-stat__meta" style={{ textAlign: 'right' }}>
            {`${fmt(c.open)} of ${fmt(matched)} open`}
          </div>
        )}
      </div>
    </div>
  );
};

SplitBar.propTypes = {
  open: PropTypes.number,
  addressed: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
Figure.propTypes = { label: PropTypes.string, value: PropTypes.string, sub: PropTypes.string, color: PropTypes.string };
CoverageBand.propTypes = { totals: PropTypes.object };
CoverageTable.propTypes = { rows: PropTypes.array, onConfigure: PropTypes.func };
CoverageInline.propTypes = {
  coverage: PropTypes.object.isRequired,
  vendorId: PropTypes.string,
  vendorName: PropTypes.string
};
